'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CardAccountSuspended } from '../components/CardAccountSuspended'
import { CardEligibilityCheck } from '../components/CardEligibilityCheck'
import { CardKYCIssue } from '../components/CardKYCIssue'
import { CardKYCPending } from '../components/CardKYCPending'
import { CardKYCRejected } from '../components/CardKYCRejected'

import { CardKYCSuccess } from '../components/CardKYCSuccess'
import { CardNotPriorityAccess } from '../components/CardNotPriorityAccess'
import { CardOnboarding } from '../components/CardOnboarding'
import { CardPriorityAccess } from '../components/CardPriorityAccess'
import { CardSessionExpired } from '../components/CardSessionExpired'
import { CardTabs } from '../components/CardTabs'
import { CardUserAgreements } from '../components/CardUserAgreements'
import { SUPPORT_URLS } from '../constants'
import { VaultClassifierProvider } from '../contexts/VaultClassifierContext'
import { useCardAuth } from '../hooks/useCardAuth'
import { useCardEligibility } from '../hooks/useCardEligibility'
import { CardEligibilityResult, CardState, KYCStatus, getCardStateFromKYCStatus } from '../types/CardState'

interface CardViewProps {
  initialEligibilityResult?: CardEligibilityResult | null
  initialSessionExpired?: boolean
  devOverrideState?: CardState | null
  isUSUser: boolean
}

export function CardView({
  initialEligibilityResult,
  initialSessionExpired,
  devOverrideState,
  isUSUser,
}: CardViewProps) {
  const {
    checkEligibility,
    checkKycStatus,
    isKycStatusLoading,
    isLoading: isEligibilityLoading,
    kycStatusError,
  } = useCardEligibility()
  const { hasTokens } = useCardAuth()

  const getInitialState = (): CardState => {
    if (initialSessionExpired) return CardState.SessionExpired
    if (initialEligibilityResult) return getCardStateFromKYCStatus(initialEligibilityResult)
    return CardState.CheckEligibility
  }

  const [currentState, setCurrentState] = useState<CardState>(getInitialState)
  const [eligibilityResult, setEligibilityResult] = useState<CardEligibilityResult | null>(
    initialEligibilityResult ?? null,
  )
  const eligibilityResultRef = useRef(eligibilityResult)
  eligibilityResultRef.current = eligibilityResult

  const [showKYCSuccess, setShowKYCSuccess] = useState(false)
  const kycSuccessTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (kycSuccessTimer.current) clearTimeout(kycSuccessTimer.current)
    }
  }, [])

  useEffect(() => {
    if (!hasTokens && currentState !== CardState.CheckEligibility && currentState !== CardState.SessionExpired) {
      if (eligibilityResult) {
        setCurrentState(CardState.SessionExpired)
      } else {
        setCurrentState(CardState.CheckEligibility)
      }
    }
  }, [hasTokens, currentState, eligibilityResult])

  const handleEligibilityChecked = (result: CardEligibilityResult) => {
    setEligibilityResult(result)
    const nextState = getCardStateFromKYCStatus(result)
    setCurrentState(nextState)
  }

  const handleGetCardNow = () => {
    setCurrentState(CardState.OnboardingKYC)
  }

  const handleOnboardingNext = useCallback((kycStatus?: KYCStatus) => {
    if (!kycStatus) return

    const updatedResult: CardEligibilityResult = {
      ...(eligibilityResultRef.current ?? { isEligible: true, isRequiredKYC: true }),
      kycStatus,
    }
    const nextState = getCardStateFromKYCStatus(updatedResult)

    setEligibilityResult(updatedResult)
    setCurrentState(nextState)

    if (nextState === CardState.KYCPending) {
      setShowKYCSuccess(true)
      if (kycSuccessTimer.current) clearTimeout(kycSuccessTimer.current)
      kycSuccessTimer.current = setTimeout(() => {
        setShowKYCSuccess(false)
      }, 4000)
    }
  }, [])

  const handleRefreshStatus = async () => {
    try {
      const kycResult = await checkKycStatus()
      const prev = eligibilityResultRef.current
      if (!prev) return

      const updatedResult = {
        ...prev,
        kycStatus: kycResult.kycStatus,
        rainStatus: kycResult.rainStatus,
      }
      const nextState = getCardStateFromKYCStatus(updatedResult)

      setEligibilityResult(updatedResult)
      setCurrentState(nextState)
    } catch (error) {
      console.error('Error refreshing KYC status:', error)
    }
  }

  const handleFullStatusCheck = async () => {
    try {
      const result = await checkEligibility(undefined)
      setEligibilityResult(result)
      const nextState = getCardStateFromKYCStatus(result)
      setCurrentState(nextState)
    } catch (error) {
      console.error('Error checking eligibility status:', error)
    }
  }

  const handleContactSupport = () => {
    window.open(SUPPORT_URLS.KYC_SUPPORT, '_blank')
  }

  const handleContactHypurrFi = () => {
    window.open(SUPPORT_URLS.KYC_ISSUE, '_blank')
  }

  const handleRetryKYC = () => {
    setCurrentState(CardState.OnboardingKYC)
  }

  const handleAuthenticationFailed = () => {
    if (eligibilityResult) {
      setCurrentState(CardState.SessionExpired)
    } else {
      setCurrentState(CardState.CheckEligibility)
    }
  }

  const handleAcceptAgreements = () => {
    const prev = eligibilityResultRef.current
    if (!prev) return

    const updatedResult = {
      ...prev,
      agreementsAccepted: true,
    }
    const nextState = getCardStateFromKYCStatus(updatedResult)

    setEligibilityResult(updatedResult)
    setCurrentState(nextState)
  }

  const effectiveState = devOverrideState ?? currentState

  const renderCurrentState = () => {
    switch (effectiveState) {
      case CardState.CheckEligibility:
        return <CardEligibilityCheck onEligibilityChecked={handleEligibilityChecked} />

      case CardState.NotEligible:
        return (
          <CardNotPriorityAccess
            waitlistEntry={eligibilityResult?.waitlistEntry}
            kycStatus={eligibilityResult?.kycStatus}
            onStartKYC={handleGetCardNow}
            onCheckStatus={handleFullStatusCheck}
            isCheckingStatus={isEligibilityLoading}
          />
        )

      case CardState.EligibleWaitlisted:
        return (
          <CardNotPriorityAccess
            waitlistEntry={eligibilityResult?.waitlistEntry}
            kycStatus={eligibilityResult?.kycStatus}
            onStartKYC={handleGetCardNow}
            onCheckStatus={handleFullStatusCheck}
            isCheckingStatus={isEligibilityLoading}
          />
        )

      case CardState.EligibleRequiresKYC:
        return (
          <CardPriorityAccess
            kycStatus={eligibilityResult?.kycStatus}
            onStartKYC={handleGetCardNow}
            onCheckStatus={handleFullStatusCheck}
            isCheckingStatus={isEligibilityLoading}
          />
        )

      case CardState.OnboardingKYC:
        return <CardOnboarding onNext={handleOnboardingNext} />

      case CardState.KYCPending:
        if (showKYCSuccess) {
          return <CardKYCSuccess />
        }
        return (
          <CardKYCPending
            kycStatus={eligibilityResult?.kycStatus}
            rainStatus={eligibilityResult?.rainStatus}
            onRefresh={handleRefreshStatus}
            isLoading={isKycStatusLoading}
            error={kycStatusError?.message}
          />
        )

      case CardState.KYCRejected:
        return <CardKYCRejected onContactSupport={handleContactSupport} />

      case CardState.KYCIssue:
        return (
          <CardKYCIssue
            kycStatus={eligibilityResult?.kycStatus}
            onContactSupport={handleContactHypurrFi}
            onRetryKYC={handleRetryKYC}
          />
        )

      case CardState.AgreementsRequired:
        return <CardUserAgreements onAcceptAgreements={handleAcceptAgreements} isUSUser={isUSUser} />

      case CardState.AccountSuspended:
        return <CardAccountSuspended accountStatus={eligibilityResult?.accountStatus} />

      case CardState.SessionExpired:
        return <CardSessionExpired onReauthenticated={handleEligibilityChecked} />

      case CardState.FullAccess:
        return <CardTabs onAuthenticationFailed={handleAuthenticationFailed} maxCards={eligibilityResult?.maxCards} />

      default:
        return <CardEligibilityCheck onEligibilityChecked={handleEligibilityChecked} />
    }
  }

  return (
    <VaultClassifierProvider>
      <div className="card-view">{renderCurrentState()}</div>
    </VaultClassifierProvider>
  )
}
